<template>
  <v-select v-bind="{
    ...$attrs,
    ...$props,
    menuProps: { contentClass: 'e-menu-content', locationStategy: 'static', ...props.menuProps },
    size: undefined,
  }" variant="outlined" center-affix single-line persistent-placeholder class="e-select" :class="classes">
    <template v-for="(_, slot) in $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope || {}" />
    </template>
    <template #label="slotProps">
      {{ slotProps.label }}<span v-if="slotProps.label && $props.required" class="text-danger300 ml-1">*</span>
    </template>
    <template #item="item">
      <v-list-item class="e-list-item" v-bind="item.props">
        <template #title>
          <span>{{ item.item.title }}</span>
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<script setup>
import { computed } from 'vue'
import { VListItem } from 'vuetify/components/VList'
import { VSelect } from 'vuetify/components/VSelect'

const props = defineProps({
  required: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  success: {
    type: Boolean,
    default: false,
  },
  hideDetails: {
    type: [Boolean, String],
    default: true,
  },
  size: {
    type: String,
    default: 'default',
    validator: (value) => ['default', 'small', 'large'].includes(value),
  },
  menuIcon: {
    type: String,
    default: 'icon-chevron-down',
  },
  menuProps: {
    type: Object,
    default: () => ({}),
  },
  label: {
    type: String,
    default: '',
  },
})
const classes = computed(() => {
  return {
    [`v-size--${props.size}`]: true,
    'e-select--with-label': props.label,
    'e-select--success': props.success,
  }
})
</script>

<style lang="scss">
@import '../../scss/styles';

.e-select {
  &--with-label {
    margin-top: 22px;
  }

  input {
    height: 100%;
  }

  .v-field--single-line .v-select__selection-text {
    display: flex;
    align-items: center;
  }

  .v-field {
    border: 1px solid $grayscale300;
    border-radius: 6px;
    transition: border-color, box-shadow, $component-transition;

    &:hover:not(.v-field--focused):not(.v-field--error) {
      transition: border-color, $component-transition;
      border-color: $grayscale500;
    }

    &--focused {
      transition: border-color, box-shadow, $component-transition;
      border-color: $primary300;
      @include blue-ring;

      .v-field__prepend-inner,
      .v-field__append-inner {
        i {
          opacity: 1;
        }
      }
    }

    &--error {
      border-color: $danger200;

      &.v-field--focused {
        @include red-ring;
      }
    }
  }

  &--success {
    .v-field {
      border-color: $success200;

      &:hover:not(.v-field--focused):not(.v-field--error) {
        border-color: $success200;
      }

      &--focused {
        @include green-ring;
      }

      .v-field__prepend-inner,
      .v-field__append-inner {
        i {
          opacity: 1;
          color: $success200;
        }
      }
    }
  }

  .v-field__input {
    padding: initial;
  }

  .v-field__outline {
    display: none;
  }

  &.v-size--small {
    .v-field__input {
      padding: 0 6px;
      min-height: 26px;
      @include body-3;
    }
  }

  &.v-size--default {
    .v-field__input {
      padding: 0 8px;
      min-height: 34px;
    }
  }

  &.v-size--large {
    .v-field__input {
      padding: 0 10px;
      min-height: 42px;
    }
  }

  .v-field--focused,
  .v-field--active {
    .v-label.v-field-label {
      visibility: visible;
    }
  }

  .v-field--center-affix .v-label.v-field-label {
    @include callout-3;

    margin: 0;
    opacity: 1;
    top: 0;
    transform: translateY(-22px);
  }

  .v-input__details {
    padding: 6px 0 0;
    @include caption-1;
  }

  .v-select__content {
    border: 1px solid $grayscale300;
  }
}


.v-menu>.e-menu-content {
  .v-list {
    padding: 6px;
    border: 1px solid $grayscale300;
    border-radius: 6px;
    box-shadow: 0px 8px 16px 0px #0334681a; //elevation-3
  }
}

.v-theme--dark {
  .e-select {
    .v-field {
      border-color: $grayscale700;

      &:hover:not(.v-field--focused):not(.v-field--error) {
        border-color: $grayscale500;
      }

      &--focused {
        border-color: $primary300;
      }

      &--error {
        border-color: $danger200;
      }
    }

    &--success {
      .v-field {
        border-color: $success200;

        &:hover:not(.v-field--focused):not(.v-field--error) {
          border-color: $success200;
        }
      }
    }

    .v-select__content {
      border-color: $grayscale700;
    }

    &.v-input--disabled {
      .v-select__selection {
        color: $grayscale700;
      }

      .v-field__append-inner {
        color: $grayscale700;
      }
    }
  }

  &.v-menu>.e-menu-content {
    .v-list {
      background: $graybase;
      border-color: $grayscale700;
    }
  }
}
</style>
